import { useEffect, useRef, useState } from 'react';
import API from '../../../../utils/constant';

/**
 * Fetches `url` through the (authenticated) axios instance and exposes it as a
 * browser object URL, so a plain <img>/<a> can use it even when the server
 * route requires the Authorization header. Works whether `url` is a public
 * media URL or an authenticated API route with the same field name — both are
 * just GETs through `API`. The object URL is revoked when `url` changes and
 * when the component unmounts.
 */
export function useBlobUrl(url: string | null | undefined): {
  objectUrl: string | null;
  loading: boolean;
  error: boolean;
} {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const activeUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (activeUrlRef.current) {
      URL.revokeObjectURL(activeUrlRef.current);
      activeUrlRef.current = null;
    }
    setObjectUrl(null);
    setError(false);

    if (!url) {
      setLoading(false);
      return;
    }

    setLoading(true);
    API.get(url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const obj = URL.createObjectURL(res.data as Blob);
        activeUrlRef.current = obj;
        setObjectUrl(obj);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Revoke on unmount (the effect above already revokes on every url change).
  useEffect(() => () => {
    if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
  }, []);

  return { objectUrl, loading, error };
}
